import { DisplayOccasionWithIcon } from '@/components/DisplayOccasionWithIcon/DisplayOccasionWithIcon';
import Dropdown from '@/components/Dropdown/Dropdown';
import Header from '@/components/Header/Header';
import StarTooltip from '@/components/Icons/CustomIcons/StarTooltip';
import Link from '@/components/Link/Link';
import ActiveBundlesTableCard from '@/pages/Franchisee/Bundles/Active/internal/ActiveBundlesTableCard';
import DescriptionCell from '@/pages/Franchisee/Bundles/Active/internal/DescriptionCell';
import { useYesNoTranslation } from '@/pages/Franchisee/Bundles/Active/internal/UseYesNoTranslation';
import { useBundlesForGroup } from '@/services/bundles/bundles.service';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useStoreBundleGroups } from '@/services/groups/groups.service';
import { useCurrentUser } from '@/services/user/user.service';
import { UserTypes } from '@/types/auth.types';
import { BundleDetails } from '@/types/bundles.types';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import LinkIcon from '@mui/icons-material/Link';
import { Typography } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import classNames from 'classnames';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './ActiveBundles.module.css';

const ActiveBundles: FC = () => {
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(undefined);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);
  const [isSticky, setSticky] = useState(false);

  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const { data: storeBundleGroupsData } = useStoreBundleGroups(true);
  const { data: dataBundleGroup } = useBundlesForGroup(!!selectedGroupId, selectedGroupId!);
  const { data: dataUser } = useCurrentUser(true);
  const navigate = useNavigate();
  const { yesNoTranslation } = useYesNoTranslation();

  const isFranchiseeUser = dataUser?.data.type === UserTypes.FRANCHISEE;

  const subNavLinks = useMemo(() => {
    const storeBundleGroups = storeBundleGroupsData?.data?.filter((group) => group.storesCount > 0) || [];

    return (
      storeBundleGroups.map((group) => ({
        title: group.name,
        to: `/bundles/active/${group.id}`,
        groupId: group.id,
      })) || []
    );
  }, [storeBundleGroupsData?.data]);

  useEffect(() => {
    const handleScroll = () => {
      const stickyValue = document.getElementById('myHeader')?.offsetTop;
      if (window.pageYOffset > (stickyValue || 0)) {
        setSticky(true);
      } else {
        setSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line no-restricted-globals
    if (subNavLinks.length > 0 && ['/bundles/active/', '/bundles/active'].includes(location.pathname)) {
      navigate(subNavLinks[0].to);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setSelectedGroupId(subNavLinks[0].groupId);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setSelectedGroup(subNavLinks[0].title);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line no-restricted-globals
  }, [subNavLinks, location.pathname, navigate]);

  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;

  const handleDropdownSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroup(event.target.value);

    const { groupId, to } = subNavLinks.find((item) => item.title === event.target.value) as {
      groupId: string;
      to: string;
    };

    setSelectedGroupId(groupId);
    navigate(to);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const DisplayStarTooltip = (id: string, bundleCode: string) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { t } = useTranslation();

    return (
      <div className={styles.starToolTipAndBundleCodeWrapper}>
        <StarTooltip content={t('activeBundles.nationalProgram')} className={styles.starSvg} position="top" />
        <Link to={`/bundles/${id}`}>{bundleCode}</Link>
      </div>
    );
  };
  // eslint-disable-next-line react/no-unstable-nested-components
  const StarTooltipCell = ({ row }: CellContext<BundleDetails, unknown>) =>
    row.original.isNationalProgramLiveOrOptIn ? (
      DisplayStarTooltip(row.original.id, row.original.code)
    ) : (
      <div className={styles.startToolTip}>
        <Link to={`/bundles/${row.original.id}`}>{row.original.code} </Link>
        {row?.original?.linkedBundleName && <LinkIcon className={styles.linkedBundleIcon} />}
      </div>
    );

  const columns = [
    {
      accessorKey: 'code',
      header: 'Bundle Code',
      enableSorting: true,
      cell: StarTooltipCell,
      contentType: 'sizeCell',
      width: '50px',
    },
    {
      accessorKey: 'name',
      header: 'NAME & DESC',
      enableSorting: true,
      cell: DescriptionCell,
      accessorFn: (originalRow: BundleDetails) => originalRow.name + originalRow.internalName,
      contentType: 'sizeCell',
    },
    {
      id: 'price',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      accessorFn: ({ chosenPrice, defaultPrice }) =>
        chosenPrice || chosenPrice === 0 || defaultPrice || defaultPrice === 0
          ? `$${chosenPrice?.toFixed(2) || defaultPrice?.toFixed(2)}`
          : '-',
      enableSorting: true,
      contentType: 'sizeCell',
    },
    {
      id: 'occasions',
      accessorKey: 'occasions',
      enableSorting: true,
      header: t('activeBundles.table.occasion'),
      cell: DisplayOccasionWithIcon,
      contentType: 'sizeCell',
      meta: { disableFilter: true },
    },

    {
      id: 'redeemOnline',
      accessorKey: 'isRedeemableOnline',
      enableSorting: true,
      header: t('activeBundles.table.redeemOnline'),
      accessorFn: (originalRow: BundleDetails) =>
        yesNoTranslation(
          originalRow.isNationalProgramLiveOrOptIn
            ? originalRow.nationalProgramRedeemableOnline
            : originalRow.isRedeemableOnline,
        ),
      contentType: 'sizeCell',
      meta: { disableFilter: true },
    },

    {
      id: 'visibleOnline',
      accessorKey: 'isVisibleOnline',
      enableSorting: true,
      header: t('activeBundles.table.visibleOnline'),
      accessorFn: (originalRow: BundleDetails) =>
        yesNoTranslation(
          originalRow.isNationalProgramLiveOrOptIn
            ? originalRow.nationalProgramVisibleOnline
            : originalRow.isVisibleOnline,
        ),
      contentType: 'sizeCell',
      meta: { disableFilter: true },
    },
  ];

  return (
    <>
      <Header
        title={t('activeBundles.title')}
        breadcrumb={{ label: t('activeBundles.breadcrumb'), to: '/' }}
        isFranchiseeUser={isFranchiseeUser}
      />
      <div
        className={classNames(styles.activeBundlesPage, styles.gridContainer, {
          [styles.withAlias]: alias !== null,
          [styles.withFreeze]: freezeExists,
        })}
      />
      <div className={styles.table}>
        <div className={styles.headerTwo}>
          <div className={styles.leftHeaderLabel}>
            <div className={styles.storeMenusText}>
              <Dropdown
                label={t('activeBundles.groupsDropdownLabel')}
                id="storeGroups"
                options={subNavLinks.map((item) => ({ value: item.title, label: item.title }))}
                value={selectedGroup}
                onChange={handleDropdownSelect}
                className={styles.dropdown}
                hidePlaceholder
              />
            </div>
            <div className={styles.warnMessage}>
              <span className={styles.fontBundleQuantity}>{dataBundleGroup?.data?.length} Bundles</span>
            </div>
          </div>
          <div className={styles.linkedBundlePosition}>
            <Typography
              sx={{
                fontFamily: 'Open Sans',
              }}
            >
              <LinkIcon
                sx={{ fontSize: '3rem' }}
                fontSize="large"
                style={{ color: '#266DCC', verticalAlign: 'middle', textAlign: 'center' }}
              />
              <span
                style={{
                  color: '#5a5a5a',
                  paddingLeft: '4px',
                  fontWeight: '600',
                  fontSize: '1.5rem',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                }}
              >
                {t('common.linkedBundles')}
              </span>
            </Typography>
          </div>
        </div>
      </div>

      <div className={styles.headerTableDivider} />

      <ActiveBundlesTableCard
        data={dataBundleGroup?.data || []}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        columns={columns}
        aliasing={alias !== null}
        enableFilters
      />
    </>
  );
};

export default ActiveBundles;
